import moment from "moment";
import * as yup from "yup";
import { Fab, Tooltip } from "@mui/material";
import htmlForPrinting from "./htmlForPrinting";
import TagTypesIcon from "../components/TagTypesIcon";
import store from "../config/configureStore";
import { showPersonTagInfo } from "../reducers/PersonDialogReducer";
import { getService } from "../reducers/service";
import { AMSReportID, serviceNames } from "./constants";
import { dateTimePicker } from "../reducers/TimeReducer";
import { decrypt } from "./encryptionDecryptionHelper";

export const formatDateToMonthDateYear = (date) => {
	return date ? dateTimePicker(date) : "";
};

export const printRef = (ref, title) => {
	const content = ref.current;
	let html = htmlForPrinting(content.innerHTML, `${title}`);
	const pri = document.getElementById("print-iframe").contentWindow;
	pri.document.open();
	pri.document.write(html);
	pri.document.close();
	pri.focus();
	pri.print();
};

export function passValidationErrors(UserName, pass) {
	const errors = [];
	const state = store.store.getState();
	const validation = state?.config?.passwordValidation || {};
	const minLength = validation.minLength ?? 8;
	const requireUppercase = validation.requireUppercase ?? true;
	const requireLowercase = validation.requireLowercase ?? true;
	const requireDigit = validation.requireDigit ?? true;
	const requireSpecialChar = validation.requireSpecialChar ?? true;
	if (UserName === pass) errors.push("Cannot be the same as User ID");
	if (pass.length < minLength)
		errors.push(`Must be at least ${minLength} characters`);
	if (pass.length > 128) errors.push("Password is too long");
	if (requireLowercase && pass.search(/[a-z]/) < 0)
		errors.push("Lower case character missing");
	if (requireUppercase && pass.search(/[A-Z]/) < 0)
		errors.push("Upper case character missing");
	if (requireDigit && pass.search(/[0-9]/) < 0)
		errors.push("Include at least one digit");
	if (
		requireSpecialChar &&
		pass.search(/[!@#\$%\^&\*\.,\-+!<>=_\(\)\[\]{};:\|\?]/) < 0
	)
		errors.push("Special character missing or invalid");

	return errors;
}

export const validateEmail = (email) => {
	const re =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(String(email).toLowerCase());
};

/**
 * returns array of filtered results - designed to work witgh DataGrid or XGrid widgets
 *
 * @param {string} filter e.g 'tango'
 * @param {array} data to be filtered
 * @param {array} columns - column definitions
 */
export const filterResults = (filter, arr, columns) => {
	filter = filter.toLocaleLowerCase();
	const result = [];
	arr.forEach((row) => {
		let match = false;
		for (let i = 0; i < columns.length - 1; i++) {
			const col = columns[i].field;
			let val = row[col];
			if (typeof val === "number") val = "" + val;
			if (typeof val === "string") {
				if (val.toLocaleLowerCase().indexOf(filter) !== -1) {
					match = true;
					break;
				}
			}
		}
		match && result.push(row);
	});
	return result;
};

/**
 * Yup test for a valid MM/DD/YYYY date format.
 * @param {string} fieldName - Name of the field (used in the error message)
 * @returns A Yup test object
 */
export const isValidDateTest = (fieldName = "Date") =>
	yup
		.string()
		.nullable()
		.test(
			"is-valid-date",
			`${fieldName} must be a valid date in the MM/DD/YYYY format`,
			(value) => {
				return !value || moment(value, "MM/DD/YYYY", true).isValid();
			},
		);

/**
 * Yup test for not being in the future.
 * Assumes value is in MM/DD/YYYY format.
 * @param {string} fieldName - Name of the field (used in the error message)
 * @returns A Yup test object
 */
export const isNotFutureDateTest = (fieldName = "Date") =>
	yup
		.string()
		.nullable()
		.test(
			"is-not-future-date",
			`${fieldName} cannot be in the future`,
			(value) => {
				return (
					!value ||
					moment(value, "MM/DD/YYYY").isSameOrBefore(moment(), "day")
				);
			},
		);

export const maxLengthMessage = (length, type = "characters") =>
	`Maximum ${length} ${type} allowed`;

export const decimalStringTest = (
	fieldName,
	intDigits = 15,
	decimalDigits = 4,
) =>
	yup
		.string()
		.trim()
		.nullable()
		.test(
			"is-valid-decimal",
			`${fieldName} must be a decimal with up to ${intDigits} digits before and ${decimalDigits} digits after the decimal point`,
			(value) => {
				if (!value) return true;
				const regex = new RegExp(
					`^\\d{1,${intDigits}}(\\.\\d{1,${decimalDigits}})?$`,
				);
				return regex.test(value);
			},
		);

export const exactLengthIfPresent = (fieldName, length) =>
	yup
		.string()
		.trim()
		.nullable()
		.test(
			"exact-length-if-present",
			`${fieldName} must be exactly ${length} characters`,
			(value) => {
				if (!value) return true;
				return value.length === length;
			},
		);

export const getYearDropdownOptions = () => {
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => {
		const year = currentYear - i;
		return { label: year.toString(), value: year };
	});
	return years;
};

export const emailFieldvalidation = yup
	.string()
	.trim()
	.test(
		"email-format",
		"Email is not in valid format",
		(value) => !value || validateEmail(value),
	)
	.max(250, maxLengthMessage(250));

/**
 * Format a string of digits into (###) ###-####
 */
export const formatPhoneNumber = (value) => {
	if (!value) return null;
	const cleaned = value.replace(/\D/g, "").slice(0, 10); // Remove non-digits and limit to 10
	const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
	if (!match) return value;
	const [, area, prefix, line] = match;
	if (area && !prefix) return `(${area}`;
	if (area && prefix && !line) return `(${area}) ${prefix}`;
	if (area && prefix && line) return `(${area}) ${prefix}-${line}`;
	return value;
};

/**
 *
 * Format a string of digits into ###-##-####
 */
export const formatSSN = (value) => {
	if (!value) return null;
	const stringValue = String(value);
	const cleaned = stringValue.replace(/\D/g, "").slice(0, 9);
	const match = cleaned.match(/^(\d{0,3})(\d{0,2})(\d{0,4})$/);
	if (!match) return value;
	const [, part1, part2, part3] = match;
	if (part1 && !part2) return `${part1}`;
	if (part1 && part2 && !part3) return `${part1}-${part2}`;
	if (part1 && part2 && part3) return `${part1}-${part2}-${part3}`;
	return cleaned;
};

export const integerValidation = (value) => {
	if (!Number.isInteger(value) || String(value).includes(".")) {
		return false;
	}
	return true;
};

/**
 * Returns human-readable days until expiration, inclusive of the expireDate.
 * @param {string|Date} expireDate
 * @returns {string}
 */
export const getDaysUntil = (expireDate) => {
	if (!expireDate) return "";
	const today = moment().startOf("day");
	const expiry = moment(expireDate).startOf("day");
	const diff = expiry.diff(today, "days") + 1;

	if (diff < 0) return "Expired";
	if (diff === 0) return "Today";
	return `${diff} day${diff === 1 ? "" : "s"}`;
};

export const formatToTwoDecimals = (number) => {
	return Number(number).toFixed(2);
};

export const formatToBase64ImageUrl = (buffer, mimeType = "image/jpeg") => {
	if (!buffer) return null;

	try {
		const uint8Array = new Uint8Array(buffer);
		let binary = "";
		for (let i = 0; i < uint8Array.length; i++) {
			binary += String.fromCharCode(uint8Array[i]);
		}
		const base64 = btoa(binary);
		return `data:${mimeType};base64,${base64}`;
	} catch (error) {
		return null;
	}
};

export const getInitials = (firstName = "", lastName = "") =>
	`${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

/**
 * Calculates age from a given date of birth.
 * @param {string | Date} dob - The date of birth in a valid format.
 * @returns {number|null} - Age in years or null if input is invalid.
 */
export const calculateAge = (dob) => {
	if (!dob || !moment(dob).isValid()) return null;

	return moment().diff(moment(dob), "years");
};

const showPersonTagDialog = (data) => {
	store.store.dispatch(showPersonTagInfo(data));
};

export const CodeTagTypeIconRender = (
	icon = 0,
	size = "",
	length = "20px",
	data = {},
	personRecord = false,
) => {
	const TagIcon = icon ? TagTypesIcon[icon] : TagTypesIcon[0];
	return (
		<Tooltip title={data.typeDescription || ""}>
			<Fab
				className="ml-1 mr-1 mt-2 mb-2"
				size={personRecord ? size : ""}
				style={
					personRecord
						? {}
						: {
								width: "32px",
								height: "10px",
								minWidth: "24px",
								padding: "0px",
							}
				}
				color="secondary"
				onClick={() => showPersonTagDialog(data)}
			>
				<TagIcon
					style={{
						width: `${length}`,
						height: `${length}`,
						fill: "#ffffff",
					}}
				/>
			</Fab>
		</Tooltip>
	);
};
export const mapDictionaryOptions = (data = []) =>
	data.map((item) => ({
		label: item.Description,
		value: item.Code,
		code: item.Code,
		description: item.Description,
		isDefault: item.IsDefault,
	}));

export const renderCodeOption = (props, option) => (
	<li {...props}>
		{option.code && option.description ? (
			<span>
				<strong>{option.code}</strong> - {option.description}
			</span>
		) : (
			<span>{option.label}</span>
		)}
	</li>
);

export const getEmployeeOptionLabel = (emp) => {
	return `${emp.employeeNum} - ${emp.lastName}, ${emp.firstName}${
		emp.middleName ? ` ${emp.middleName}` : ""
	}`;
};
export function isValidDate(date) {
	if (typeof date !== "string" || date.length > 24) return false;
	var dateReg = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
	if (date.match(dateReg) !== null && moment(date).isValid()) return true;
	return false;
}

export function sortObjArr(arr, sortBy, sortDirection = "ASC") {
	if (!Array.isArray(arr)) return arr;
	const newArr = [...arr];
	newArr.sort((a, b) => {
		let valA = a[sortBy];
		let valB = b[sortBy];
		if (isValidDate(valA) && isValidDate(valB)) {
			if (sortDirection === "ASC") {
				return moment(valB).valueOf() - moment(valA).valueOf();
			} else {
				return moment(valA).valueOf() - moment(valB).valueOf();
			}
		} else {
			if (typeof valA === "string" && typeof valB === "string") {
				valA = valA.toLocaleLowerCase();
				valB = valB.toLocaleLowerCase();
			}
			if (sortDirection === "ASC") {
				return valA < valB ? -1 : valA > valB ? 1 : 0;
			} else {
				return valA > valB ? -1 : valA < valB ? 1 : 0;
			}
		}
	});
	return newArr;
}

export function addCreatedUpdatedBy(data) {
	const state = store.store.getState();
	const Username = state?.user?.userData?.user?.Username;
	const exportData = { ...data };
	if (Username) {
		exportData.CreatedBy = Username;
		exportData.UpdatedBy = Username;
	}
	return exportData;
}

export const toTitleCase = (str) => {
	if (typeof str !== "string") return "";
	return str
		.split("-")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

export const getRowsPerPageOptions = (total, baseOptions = [5, 10, 20]) => {
	if (total && total > Math.max(...baseOptions)) {
		return [...baseOptions, total];
	}
	return baseOptions;
};

export const isBetween = (date, start, end) => date >= start && date <= end;
export const getTotal = (weekData) =>
	Object.values(weekData).reduce((sum, val) => sum + val, 0);

export const startOfThisWeek = moment().startOf("week");
export const endOfThisWeek = moment().endOf("week");

export const startOfLastWeek = moment().startOf("week").subtract(1, "week");
export const endOfLastWeek = moment().endOf("week").subtract(1, "week");

export const startOfNextWeek = moment().startOf("week").add(1, "week");
export const endOfNextWeek = moment().endOf("week").add(1, "week");

export const today = moment();
export const next30DaysDate = moment(today).add(30, "days");
export const last30DaysDate = moment(today).subtract(30, "days");

export const areAllPropsEmpty = (obj) => {
	return Object.values(obj).every(
		(value) =>
			value === "" ||
			value === null ||
			value === undefined ||
			value === false ||
			(Array.isArray(value) && value.length === 0) ||
			(typeof value === "object" &&
				!Array.isArray(value) &&
				value !== null &&
				Object.keys(value).length === 0),
	);
};

export const getUserNameById = (users, id) => {
	if (!id || !users) return "—";
	return users[id] || "—";
};

export const printAMSReport = async (
	reportId,
	perPersonnelID,
	userAgency,
	permissions,
	ReportingOfficer,
) => {
	const service = getService(serviceNames.amsReportViewer);
	const perRecordId = decrypt(perPersonnelID);
	const printData = {
		reportId: reportId,
		queryParams: {
			AgencyID: userAgency,
			ReportingOfficer: ReportingOfficer,
		},
		payload: permissions,
	};

	if (reportId === AMSReportID.Employees) {
		printData.queryParams["perPersonnelID"] = perRecordId;
	} else if (reportId === AMSReportID.Applicants) {
		printData.queryParams["perApplicantID"] = perRecordId;
	} else if (reportId === AMSReportID.Courses) {
		printData.queryParams["perTrainingCourseID"] = perRecordId;
	} else if (reportId === AMSReportID.TrainingRecords) {
		printData.queryParams["perTrainingID"] = perRecordId;
	} else if (reportId === AMSReportID.Certifications) {
		printData.queryParams["perTrainingCertificationID"] = perRecordId;
	}

	const result = await service.create(printData);

	return result || "";
};

export const getCodeId = (id, codeList) => {
	return codeList.find((as) => as.Code === id)?.ptsCodeId || null;
};

export const removeDuplicateData = (data) => {
	const uniqueList = Array.from(
		new Map(data.map((item) => [item.id, item])).values(),
	);

	return uniqueList;
};

export const allowNumericInputOnly = (e) => {
	if (!/[0-9]/.test(e.key)) {
		e.preventDefault();
	}
};

export const allowNumericPasteInputOnly = (e) => {
	const pastedData = e.clipboardData.getData("Text") || "";
	if (!/^[0-9()\-\s]+$/.test(pastedData)) {
		e.preventDefault();
	}
};

export const checkAPIVersion = (currentVersion, minVersion) => {
	const [cMajor = 0, cMinor = 0] = currentVersion.split(".").map(Number);
	const [mMajor = 0, mMinor = 0] = minVersion.split(".").map(Number);

	if (cMajor > mMajor) return true;
	if (cMajor < mMajor) return false;

	if (cMinor > mMinor) return true;
	if (cMinor < mMinor) return false;

	return true;
};

export const getMileageWarnType = (entered, current) => {
	const n = parseFloat(entered);
	if (!entered || isNaN(n) || !current) return null;
	if (n < current) return "low";
	if (n > current + 200) return "high";
	return null;
};

export const daysBetween = (d1, d2 = new Date()) => {
	const diff = new Date(d2) - new Date(d1);
	return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const isInspectionOverdue = (dateStr) => {
	if (!dateStr) return true;
	return daysBetween(dateStr) > 180;
};

export const fmtDate = (iso) => {
	if (!iso) return "—";
	// For "yyyy-mm-dd" strings, parse directly to avoid UTC→local shift
	const dateOnly = String(iso).slice(0, 10);
	const parts = dateOnly.split("-");
	if (parts.length === 3) {
		const [y, m, d] = parts;
		if (y && m && d)
			return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
	}
	// Fallback for ISO datetime strings — use UTC methods
	const dt = new Date(iso);
	if (isNaN(dt)) return "—";
	return `${String(dt.getUTCMonth() + 1).padStart(2, "0")}/${String(dt.getUTCDate()).padStart(2, "0")}/${dt.getUTCFullYear()}`;
};

export const fmtDateTime = (iso) => {
	if (!iso) return "—";
	const d = new Date(iso);
	if (isNaN(d)) return "—";
	// Use local time for datetime display (user's clock)
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	const year = d.getFullYear();
	const h = d.getHours();
	const min = String(d.getMinutes()).padStart(2, "0");
	const ampm = h >= 12 ? "PM" : "AM";
	const h12 = h % 12 || 12;
	return `${month}/${day}/${year} ${h12}:${min} ${ampm}`;
};

export const StatusBadge = ({ status }) => {
	const map = {
		"In Service": "badge-green",
		"Out of Service": "badge-red",
		Maintenance: "badge-yellow",
		Retired: "badge-gray",
		Open: "badge-yellow",
		Closed: "badge-green",
		Deployed: "badge-blue",
		Stored: "badge-gray",
		Active: "badge-green",
		Inactive: "badge-gray",
		Excellent: "badge-green",
		Good: "badge-blue",
		Fair: "badge-yellow",
		Poor: "badge-red",
		Preventative: "badge-blue",
		Repair: "badge-red",
		Inspection: "badge-purple",
		Pass: "badge-green",
		Fail: "badge-red",
		"Needs Attention": "badge-yellow",
		Pending: "badge-yellow",
		Scheduled: "badge-blue",
		"In Progress": "badge-yellow",
		Completed: "badge-green",
		Expired: "badge-gray",
		Cancelled: "badge-red",
		Awarded: "badge-purple",
		"Pre-Shift": "badge-blue",
		"Post-Shift": "badge-purple",
		Weekly: "badge-gray",
		Special: "badge-yellow",
	};
	const cls = map[status] || "badge-gray";
	return (
		<span className={`badge ${cls}`}>
			<span className="badge-dot" />
			{status}
		</span>
	);
};

export const getServiceStatus = (vehicle, req, workOrders) => {
	const DUE_SOON_PCT = 0.1; // warn when within 10% of threshold
	const fulfilled = workOrders
		.filter(
			(w) =>
				w.vehicleID === vehicle.id &&
				w.serviceRequirementID === req.id &&
				w.status === "Closed" &&
				w.closedDate,
		)
		.sort((a, b) => new Date(b.closedDate) - new Date(a.closedDate));

	const last = fulfilled[0] || null;

	if (req.unit === "miles") {
		const threshold = req.numericValue;
		const lastOdo = last?.odometer || vehicle.startingMileage || 0;
		const milesSince = (vehicle.odometer || 0) - lastOdo;
		const remaining = threshold - milesSince;
		const dueSoonThreshold = threshold * DUE_SOON_PCT;
		return {
			status:
				remaining <= 0
					? "Overdue"
					: remaining <= dueSoonThreshold
						? "Due Soon"
						: "OK",
			dueIn: remaining,
			unit: "mi",
			lastServiceDate: last?.closedDate || null,
			lastServiceOdometer: lastOdo,
			milesSince,
		};
	} else {
		const threshold = req.numericValue;
		const lastDate = last?.closedDate || vehicle.purchaseDate || null;
		const daysSince = lastDate ? daysBetween(lastDate) : 9999;
		const remaining = threshold - daysSince;
		const dueSoonThreshold = threshold * DUE_SOON_PCT;
		return {
			status:
				remaining <= 0
					? "Overdue"
					: remaining <= dueSoonThreshold
						? "Due Soon"
						: "OK",
			dueIn: remaining,
			unit: "days",
			lastServiceDate: lastDate,
			daysSince,
		};
	}
};

export const activeCodes = (category, codes) =>
	(codes[category] || [])
		.filter((c) => c.isActive !== false)
		.map((c) => c.value);
